import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Key, Search, Filter, Plus, CheckCircle, XCircle, Clock, 
  RefreshCw, Download, ChevronDown, ChevronUp, Eye, Edit,
  AlertCircle, Shield, Users, Fingerprint, Calendar, Mail,
  Building2, Usb, Check, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LicenseAdminPage = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ active: 0, lifetime: 0, subscribers: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  useEffect(() => {
    fetchLicenses();
  }, [searchQuery, typeFilter, statusFilter]);

  const fetchLicenses = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (typeFilter) params.append('license_type', typeFilter);
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`${API_URL}/api/admin/usb-licenses?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLicenses(data.licenses || []);
        setStats(data.stats || { active: 0, lifetime: 0, subscribers: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch licenses:', err);
      toast.error('Failed to load licenses');
    } finally {
      setLoading(false);
    }
  };

  const verifyLicense = async (licenseId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/usb-licenses/${licenseId}/verify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setShowVerifyModal(data);
        toast.success(data.is_valid ? 'License is valid!' : 'License has issues');
      }
    } catch (err) {
      toast.error('Verification failed');
    }
  };

  const getStatusBadge = (license) => {
    const status = license.status || 'active';
    const isExpired = license.expiry_date && new Date(license.expiry_date) < new Date();
    
    if (isExpired) {
      return <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">Expired</span>;
    }
    
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">Active</span>;
      case 'suspended':
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Suspended</span>;
      case 'revoked':
        return <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">Revoked</span>;
      default:
        return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-full">{status}</span>;
    }
  };

  const getLicenseTypeBadge = (type) => {
    const badges = {
      'LT32': { label: 'Lifetime 32GB', color: 'bg-emerald-500/20 text-emerald-400' },
      'LT16': { label: 'Lifetime 16GB', color: 'bg-emerald-500/20 text-emerald-400' },
      'SUB32': { label: 'Subscriber 32GB', color: 'bg-blue-500/20 text-blue-400' },
      'SUB16': { label: 'Subscriber 16GB', color: 'bg-blue-500/20 text-blue-400' },
      'TRIAL': { label: 'Trial', color: 'bg-yellow-500/20 text-yellow-400' }
    };
    const badge = badges[type] || { label: type, color: 'bg-gray-500/20 text-gray-400' };
    return <span className={`px-2 py-1 ${badge.color} text-xs rounded-full`}>{badge.label}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Lifetime';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-900 relative" data-testid="license-admin-page">
      {/* Background */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/07e51985-c374-4472-a4ec-7832a182bb43/images/a599bfb20d8b730453411fb0496e41df29ac3936a599b1146f16c28f94161081.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div className="fixed inset-0 z-0 bg-slate-900/90" />
      
      {/* Content */}
      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Key className="w-7 h-7 text-blue-400" />
              USB License Management
            </h1>
            <p className="text-gray-400 mt-1">Manage and verify USB-based licenses</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchLicenses}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium flex items-center gap-2 transition"
            >
              <Plus className="w-5 h-5" />
              New License
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.active}</p>
                <p className="text-gray-400 text-sm">Active Licenses</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.lifetime}</p>
                <p className="text-gray-400 text-sm">Lifetime Licenses</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.subscribers}</p>
                <p className="text-gray-400 text-sm">Subscribers</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Usb className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{licenses.length}</p>
                <p className="text-gray-400 text-sm">Total Licenses</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by License ID, Name, or Email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                data-testid="license-search"
              />
            </div>
            
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="LT32">Lifetime 32GB</option>
              <option value="LT16">Lifetime 16GB</option>
              <option value="SUB32">Subscriber 32GB</option>
              <option value="SUB16">Subscriber 16GB</option>
              <option value="TRIAL">Trial</option>
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>
        </div>

        {/* Licenses Table */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Loading licenses...</p>
            </div>
          ) : licenses.length === 0 ? (
            <div className="p-12 text-center">
              <Key className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">No licenses found</h3>
              <p className="text-gray-400">Create your first USB license to get started.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-white/5 border-b border-white/10">
                  <th className="text-left text-gray-400 font-medium p-4">License ID</th>
                  <th className="text-left text-gray-400 font-medium p-4">Customer</th>
                  <th className="text-left text-gray-400 font-medium p-4">Type</th>
                  <th className="text-left text-gray-400 font-medium p-4">Status</th>
                  <th className="text-left text-gray-400 font-medium p-4">Issue Date</th>
                  <th className="text-left text-gray-400 font-medium p-4">Expiry</th>
                  <th className="text-right text-gray-400 font-medium p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((license) => (
                  <tr 
                    key={license.license_id} 
                    className="border-b border-white/5 hover:bg-white/5 transition"
                    data-testid={`license-row-${license.license_id}`}
                  >
                    <td className="p-4">
                      <code className="text-blue-400 text-sm bg-blue-500/10 px-2 py-1 rounded">
                        {license.license_id}
                      </code>
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="text-white font-medium">{license.customer_name}</p>
                        <p className="text-gray-400 text-sm">{license.customer_email}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      {getLicenseTypeBadge(license.license_type)}
                    </td>
                    <td className="p-4">
                      {getStatusBadge(license)}
                    </td>
                    <td className="p-4 text-gray-300">
                      {formatDate(license.issue_date)}
                    </td>
                    <td className="p-4">
                      {license.is_lifetime ? (
                        <span className="text-emerald-400 flex items-center gap-1">
                          <Shield className="w-4 h-4" />
                          Lifetime
                        </span>
                      ) : (
                        <span className="text-gray-300">{formatDate(license.expiry_date)}</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => verifyLicense(license.license_id)}
                          className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 transition"
                          title="Verify License"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setSelectedLicense(license)}
                          className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-400 transition"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* License Details Modal */}
        {selectedLicense && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
            <div className="bg-slate-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Key className="w-5 h-5 text-blue-400" />
                  License Details
                </h3>
                <button 
                  onClick={() => setSelectedLicense(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">License ID</p>
                  <code className="text-blue-400 text-lg">{selectedLicense.license_id}</code>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-1">Customer Name</p>
                    <p className="text-white">{selectedLicense.customer_name}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-1">Email</p>
                    <p className="text-white">{selectedLicense.customer_email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-1">License Type</p>
                    {getLicenseTypeBadge(selectedLicense.license_type)}
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-1">Status</p>
                    {getStatusBadge(selectedLicense)}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-1">Issue Date</p>
                    <p className="text-white">{formatDate(selectedLicense.issue_date)}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-1">Expiry Date</p>
                    <p className="text-white">
                      {selectedLicense.is_lifetime ? 'Lifetime' : formatDate(selectedLicense.expiry_date)}
                    </p>
                  </div>
                </div>

                {selectedLicense.organization && (
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-1">Organization</p>
                    <p className="text-white">{selectedLicense.organization}</p>
                  </div>
                )}

                {selectedLicense.notes && (
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-gray-400 text-sm mb-1">Notes</p>
                    <p className="text-white">{selectedLicense.notes}</p>
                  </div>
                )}

                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">USB Serial Hash</p>
                  <code className="text-gray-300 text-xs break-all">{selectedLicense.usb_serial_hash}</code>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => verifyLicense(selectedLicense.license_id)}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Verify License
                </button>
                <button
                  onClick={() => setSelectedLicense(null)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Verify Result Modal */}
        {showVerifyModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
            <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full">
              <div className="text-center">
                {showVerifyModal.is_valid ? (
                  <>
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">License Valid ✓</h3>
                    <p className="text-gray-400 mb-4">This license is active and valid.</p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <XCircle className="w-10 h-10 text-red-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">License Invalid</h3>
                    <div className="text-left bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                      <p className="text-red-400 text-sm font-medium mb-2">Issues found:</p>
                      <ul className="text-red-300 text-sm space-y-1">
                        {showVerifyModal.issues?.map((issue, i) => (
                          <li key={i}>• {issue}</li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
                
                <div className="bg-white/5 rounded-lg p-3 text-left mb-4">
                  <p className="text-gray-400 text-xs">License ID</p>
                  <code className="text-blue-400 text-sm">{showVerifyModal.license_id}</code>
                </div>

                <button
                  onClick={() => setShowVerifyModal(null)}
                  className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LicenseAdminPage;
